import { getEarliestStayStart, isPastDate, startOfDay } from "./dateFieldUtils";

/**
 * Validates min/max range values
 * @param {number|string} min - Minimum value
 * @param {number|string} max - Maximum value
 * @param {string} errorMessage - Error message if validation fails
 * @returns {string|null} Error message or null if valid
 */
function validateMinMaxRange(min, max, errorMessage) {
  if (!min || !max) return null;

  const minNum = Number(min);
  const maxNum = Number(max);

  return minNum > maxNum ? errorMessage : null;
}

/**
 * True when an already-started promo's start date is locked in the UI (HTT-9780). The user can
 * only extend the end date, so flagging the start would block a save they cannot possibly fix.
 * A start still in the future stays editable, and so stays validated.
 */
function isStartLocked(isEditingExistingPromo, startDate) {
  return isEditingExistingPromo && !!startDate && isPastDate(startDate);
}

/** A start date that has slipped into the past, unless the UI has locked it. */
function pastStartError(shown, startDate, locked, message) {
  if (!shown || !startDate || locked) return null;
  return isPastDate(startDate) ? message : null;
}

/**
 * One end of a date condition set without the other. The split fields (HTT-9780) can emit one end
 * at a time, and an incomplete pair is written to the promo as nulls — so without this the
 * condition would be dropped on save with no explanation. Only applies in edit mode, where those
 * fields render; creation uses a range picker, which cannot produce a half-filled pair.
 */
function incompletePairError(applies, start, end, message) {
  if (!applies) return null;
  return !!start !== !!end ? message : null;
}

/**
 * Stay dates starting before the booking window opens. Skipped only when BOTH starts are locked —
 * the ordering between them is then historical fact and equally unfixable. If either is still
 * editable, it holds.
 */
function stayBeforeBookingError({
  showBooking,
  showPSD,
  fromBooking,
  stayStartDate,
  bookingStartLocked,
  stayStartLocked,
}) {
  if (!showBooking || !showPSD || !fromBooking || !stayStartDate) return null;
  if (bookingStartLocked && stayStartLocked) return null;
  return startOfDay(stayStartDate) < startOfDay(fromBooking)
    ? "Stay dates cannot be before booking dates"
    : null;
}

const STAY_PAST_ERROR = "Stay dates cannot be in the past";
const STAY_INCOMPLETE_ERROR =
  "Each stay date range needs both a start date and an end date.";
const STAY_OVERLAP_ERROR =
  "These dates overlap with an existing Stay Date range — use Blackout Dates to exclude specific days within a larger stay window.";
const STAY_ADJACENT_ERROR =
  "These date ranges are adjacent with no gap — consider extending the existing range instead of adding a new one.";
const BLACKOUT_OUTSIDE_STAY_ERROR =
  "Blackout dates must fall within a Stay Date range.";

/**
 * Blackout ranges outside every Stay Date range (EO-1492 Feature 3). Each
 * filled blackout pair must sit entirely within a single filled stay range —
 * one spanning the gap between two ranges fails. Only applies when at least
 * one filled stay range exists: with no stay window the promo is evergreen
 * and a blackout may sit anywhere (today's behaviour — anything stricter
 * would block saving existing promos that have blackouts but no stay dates).
 * Locked (elapsed) stay ranges still count as containers, so editing an old
 * promo with a historical blackout is not blocked.
 */
function blackoutOutsideStayError(blackoutDates, stayDateRanges) {
  const stays = (stayDateRanges || []).filter(
    (r) => r.dates?.[0] && r.dates?.[1]
  );
  const blackouts = (blackoutDates || []).filter(
    (b) => b.dates?.[0] && b.dates?.[1]
  );
  if (stays.length === 0 || blackouts.length === 0) return null;

  const isContained = (b) =>
    stays.some(
      (s) =>
        startOfDay(b.dates[0]) >= startOfDay(s.dates[0]) &&
        startOfDay(b.dates[1]) <= startOfDay(s.dates[1])
    );
  return blackouts.every(isContained) ? null : BLACKOUT_OUTSIDE_STAY_ERROR;
}

/**
 * Past-date check for a single stay range. Locked (elapsed) ranges are skipped
 * by the caller. An in-progress range (start already past, end still to come) is
 * not marked isLocked — parseStayDateRanges only locks a range once its END has
 * passed. When editing an existing promo the UI disables the past start
 * (beforeToday), so the user cannot move it — blocking save would strand them
 * (HTT-9780). Mirror isStartLocked/pastStartError on the booking & single-range
 * paths and skip the past-date checks for such a range.
 */
function stayRangePastError(range, todayMs, isEditingExistingPromo) {
  const startMs = startOfDay(range.dates[0]);
  if (isEditingExistingPromo && startMs < todayMs) return null;
  if (startMs < todayMs) return STAY_PAST_ERROR;
  if (range.dates?.[1] && startOfDay(range.dates[1]) < todayMs) {
    return STAY_PAST_ERROR;
  }
  return null;
}

/** True when `nextStartMs` is the day immediately after `endDate` (no gap). */
function isNextDay(endDate, nextStartMs) {
  const dayAfter = new Date(endDate);
  dayAfter.setHours(0, 0, 0, 0);
  dayAfter.setDate(dayAfter.getDate() + 1);
  return dayAfter.getTime() === nextStartMs;
}

/**
 * Overlap / no-gap-adjacency check across all filled ranges. Adjacency is only
 * flagged when the earlier range has not already elapsed.
 */
function stayRangeConflictError(ranges, todayMs) {
  const filled = ranges.filter((r) => r.dates?.[0] && r.dates?.[1]);
  if (filled.length < 2) return null;

  const sorted = [...filled].sort(
    (a, b) => startOfDay(a.dates[0]) - startOfDay(b.dates[0])
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const currEndMs = startOfDay(sorted[i].dates[1]);
    const nextStartMs = startOfDay(sorted[i + 1].dates[0]);

    if (currEndMs >= nextStartMs) return STAY_OVERLAP_ERROR;

    const adjacentNotElapsed =
      isNextDay(sorted[i].dates[1], nextStartMs) && currEndMs >= todayMs;
    if (adjacentNotElapsed) return STAY_ADJACENT_ERROR;
  }

  return null;
}

/**
 * Validates an array of stay date ranges (EO-1492).
 *
 * Checks in order:
 * 1. Incomplete range (one or both dates blank) — blocks save so the user
 *    completes or removes it, since incomplete ranges are dropped from the
 *    API payload and would otherwise vanish silently (EO-1492). A single
 *    fully-blank range is the "evergreen" case (section on, no specific
 *    window) and stays valid.
 * 2. Past date for non-locked ranges (relaxed in edit mode — see stayRangePastError)
 * 3. Overlap between any two ranges
 * 4. Adjacency with no gap, unless the earlier range's end is already elapsed
 *
 * @param {Array<{dates: Date[]|null, isLocked: boolean}>} ranges
 * @param {boolean} [isEditingExistingPromo] - Editing an existing promo
 * @returns {string|null} Error message, or null if valid
 */
function validateStayDateRanges(ranges, isEditingExistingPromo = false) {
  const todayMs = startOfDay(new Date());

  // A lone blank range means "no specific stay window" (evergreen) and is
  // allowed. Once there is a second range or a half-filled one, every range
  // must be complete — an incomplete one is dropped from the payload, so block
  // the save until it is completed or removed (EO-1492).
  const isEvergreen =
    ranges.length === 1 &&
    !ranges[0].isLocked &&
    !ranges[0].dates?.[0] &&
    !ranges[0].dates?.[1];
  const hasIncompleteRange =
    !isEvergreen &&
    ranges.some((r) => !r.isLocked && !(r.dates?.[0] && r.dates?.[1]));
  if (hasIncompleteRange) return STAY_INCOMPLETE_ERROR;

  for (const r of ranges) {
    if (r.isLocked || !r.dates?.[0]) continue;
    const pastError = stayRangePastError(r, todayMs, isEditingExistingPromo);
    if (pastError) return pastError;
  }

  return stayRangeConflictError(ranges, todayMs);
}

/**
 * Validates targeting conditions for promotions
 * @param {Object} params - Validation parameters
 * @param {number} params.minLos - Minimum length of stay
 * @param {number} params.maxLos - Maximum length of stay
 * @param {number} params.minLeadTime - Minimum booking lead time
 * @param {number} params.maxLeadTime - Maximum booking lead time
 * @param {boolean} params.showBooking - Whether booking window is shown
 * @param {Date} params.fromBooking - Booking start date
 * @param {Date} params.toBooking - Booking end date
 * @param {boolean} params.showPSD - Whether stay dates are shown
 * @param {Date} [params.stayStartDate] - Stay start date (single-range legacy path)
 * @param {Date} [params.stayEndDate] - Stay end date (single-range legacy path)
 * @param {Array} [params.stayDateRanges] - Multi-range stay dates (EO-1492); when present
 *   overrides single-range validation for stay dates
 * @param {Array} [params.blackoutDates] - Blackout rows (`{dates: Date[]|null}`); each filled
 *   pair must fall within one stay date range (EO-1492 Feature 3)
 * @param {boolean} [params.isEditingExistingPromo] - Editing an existing promo
 * @returns {string[]} Array of validation error messages
 */
export function validateTargetingConditions({
  minLos,
  maxLos,
  minLeadTime,
  maxLeadTime,
  showBooking,
  fromBooking,
  toBooking,
  showPSD,
  stayStartDate,
  stayEndDate,
  stayDateRanges,
  blackoutDates,
  isEditingExistingPromo = false,
}) {
  const bookingStartLocked = isStartLocked(isEditingExistingPromo, fromBooking);
  const stayStartLocked = isStartLocked(isEditingExistingPromo, stayStartDate);

  const hasRanges = showPSD && stayDateRanges?.length > 0;

  // For "stay before booking": use earliest unlocked range start, or legacy stayStartDate.
  const effectiveStayStart = hasRanges
    ? getEarliestStayStart(stayDateRanges)
    : stayStartDate;

  // Lock status of the start that actually drives the stay-before-booking check.
  // For multi-range this is the earliest unlocked range start; when editing an
  // existing promo whose start has already elapsed, the UI disables that start
  // (beforeToday), so an unfixable ordering must not block the save (HTT-9780) —
  // mirror the single-range path instead of assuming it is never locked.
  const effectiveStayStartLocked = hasRanges
    ? isStartLocked(isEditingExistingPromo, effectiveStayStart)
    : stayStartLocked;

  return [
    validateMinMaxRange(
      minLos,
      maxLos,
      "Minimum Length of Stay cannot be greater than Maximum Length of Stay"
    ),
    validateMinMaxRange(
      minLeadTime,
      maxLeadTime,
      "Minimum Booking Lead Time cannot be greater than Maximum Booking Lead Time"
    ),
    pastStartError(
      showBooking,
      fromBooking,
      bookingStartLocked,
      "Booking dates cannot be in the past"
    ),
    // Single-range stay: only when stayDateRanges is not in use
    !hasRanges
      ? pastStartError(
          showPSD,
          stayStartDate,
          stayStartLocked,
          "Stay dates cannot be in the past"
        )
      : null,
    // Multi-range stay validations (overlap, adjacency, past date)
    hasRanges
      ? validateStayDateRanges(stayDateRanges, isEditingExistingPromo)
      : null,
    // Blackouts must sit inside a stay range (EO-1492 Feature 3); inert when
    // the stay section is off, since there is then no window to violate.
    blackoutOutsideStayError(blackoutDates, hasRanges ? stayDateRanges : []),
    incompletePairError(
      isEditingExistingPromo && showBooking,
      fromBooking,
      toBooking,
      "Booking window needs both a start date and an end date"
    ),
    // Single-range stay pair check: not applicable with multi-range (pickers fill both ends)
    !hasRanges
      ? incompletePairError(
          isEditingExistingPromo && showPSD,
          stayStartDate,
          stayEndDate,
          "Stay dates need both a start date and an end date"
        )
      : null,
    stayBeforeBookingError({
      showBooking,
      showPSD,
      fromBooking,
      stayStartDate: effectiveStayStart,
      bookingStartLocked,
      stayStartLocked: effectiveStayStartLocked,
    }),
  ].filter(Boolean);
}
